import React from 'react';
import dayjs from 'dayjs';
import { createUseStyles } from 'react-jss';
import { DayButtonProps, DayPicker, WeekdayProps } from 'react-day-picker';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CheckinState, ISO8601Month } from '../model/CheckinState';
import { FeelingIcon } from '../components/FeelingIcon';
import { AddFeelingIcon } from '../../theme/Icons';

const useStyles = createUseStyles({
    [`@media (max-width: 600px)`]: {
        picker: {
            '--rdp-day_button-width': '100%'
        }
    },

    picker: {
        '--rdp-day-height': '75px',
        '--rdp-today-color': 'var(--ion-color-primary)',
        '--rdp-weekday-padding': '0px',

        '& div.rdp-months': {
            minWidth: '100%',

            '& div.rdp-month_caption': {
                display: 'none'
            },

            '& div.rdp-month': {
                width: '100%',

                '& table.rdp-month_grid': {
                    width: '100%',
                    maxWidth: '100%',

                    '& th.rdp-weekday': {
                        fontSize: 14
                    },

                    '& td.rdp-day': {
                        justifyItems: 'center'
                    }
                }
            }
        }
    },

    addFeelingIcon: {
        opacity: 0.4,

        '&:hover': {
            opacity: 1,

            '& ion-text': {
                color: 'var(--ion-color-primary)'
            }
        }
    },

    futureMarker: {
        width: 30,
        height: 30,
        margin: 2,
        border: '1px dashed var(--ion-color-step-350)',
        borderRadius: '50%'
    }
});

const CustomWeekday = (props: WeekdayProps) => {
    // 3 letter day headings
    const label = (props['aria-label'] || '').substring(0, 3);
    return (
        <th {...props}>{label}</th>
    );
};

const CustomDayButton = (props: DayButtonProps) => {
    const classes = useStyles();
    const checkinState = useInstance(CheckinState);

    const { day, modifiers, ...buttonProps } = props;
    const checkin = checkinState.getFinalCheckin({ date: dayjs(day.date).toISOString(), checkinType: 'personal' });
    const today = dayjs().startOf('day');
    const theDay = dayjs(day.date).startOf('day');
    const isToday = theDay.isToday();
    const isFuture = theDay.isAfter(today, 'day');

    return (
        <button {...buttonProps} style={{ whiteSpace: 'nowrap', flexDirection: 'column', cursor: isFuture ? 'default': 'pointer' }}>
            <div>
                {checkin?.feeling ? (
                    <FeelingIcon solid feeling={checkin.feeling} size="xlarge" />
                ) : isFuture ? (
                    <div className={classes.futureMarker} />
                ) : (
                    <span className={!isToday ? classes.addFeelingIcon : ''}>
                        <AddFeelingIcon size="xlarge" color={isToday ? 'primary' : 'medium'} />
                    </span>
                )}
            </div>
            <div style={{fontWeight: isToday ? 'bold' : undefined, marginTop: isToday ? -1 : undefined}}>
                {props.children}
            </div>
        </button>
    );
};

export interface IJournalCalendarProps {
    month: ISO8601Month, // YYYY-MM
    onDayClick: (date: ISO8601Month) => void;
}

export const JournalCalendar: React.FC<IJournalCalendarProps> = ({ onDayClick, month }) => {
    const classes = useStyles();

    // DayPicker uses Date objects, so convert to/from ISO8601
    const monthAsDate = dayjs(month).toDate();
    const handleDayClick = (day: Date) => onDayClick(dayjs(day).toISOString());

    return (
        <DayPicker
            className={classes.picker}
            hideNavigation
            disableNavigation
            month={monthAsDate}
            onDayClick={handleDayClick}
            components={{
                Weekday: CustomWeekday,
                DayButton: CustomDayButton
            }}
        />
    );
};
